import React, { useMemo } from 'react';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import useNotification from 'containers/App/Notifications/useNotification';
import { EleveForm } from 'containers/Eleves/List/components/EleveForm';
import _last from 'lodash-es/last';
import useCreateEleve from 'queries/eleves/useCreateEleve';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import Dialog from 'components/Dialog';
import { useSchoolGoTo } from 'containers/App/useSchoolGoTo';
import { useTranslation } from 'react-i18next';
import { useEleveCount, useElevesData } from 'queries/eleves/useEleves';
import {
  useCloseAddEleveModal,
  useGetAddElevePayload,
  useIsOpenAddEleveModal,
} from 'containers/Eleves/AddEleveDialog/atoms';

interface BodyProps {
  onClose: () => void;
}

function AddEleveDialogBody(props: BodyProps) {
  const { onClose } = props;
  const payload = useGetAddElevePayload();
  const { data } = useElevesData();
  const eleves = data || [];

  const { notifySuccess } = useNotification();
  const { t } = useTranslation(['eleve', 'common']);
  const classrooms = useClassrooms();
  const createEleve = useCreateEleve();

  // Prevent the form from being reset when the viewId changes
  const initialValues = useMemo(
    () => ({
      birthday: _last(eleves.map(el => el.birthday).filter(d => d)),
      gender: 'H',
      decloisonement: null,
      classroomId:
        (classrooms || []).length > 0
          ? _last(eleves.map(el => el.classroomId).filter(d => d)) ||
            classrooms[0].id
          : undefined,
      groups: [],
    }),
    [payload?.viewId],
  );

  return (
    <EleveForm
      viewId={payload?.viewId!}
      onClose={onClose}
      initialValues={initialValues}
      onSubmit={async eleve => {
        await createEleve({ eleves: [{ ...eleve, id: uuid() }] });
        notifySuccess({
          title: t('eleve:messages.addEleveSuccess'),
        });
      }}
    />
  );
}

export function AddEleveDialog() {
  const { data: nbOfEleves } = useEleveCount();
  const isOpen = useIsOpenAddEleveModal();
  const close = useCloseAddEleveModal();
  const navigate = useNavigate();
  const { t } = useTranslation(['eleve', 'common']);
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const goTo = useSchoolGoTo();

  const onClose = () => {
    if (nbOfEleves === 0) {
      navigate(-1);
      close();
      return;
    }
    if (redirectParam) {
      goTo(redirectParam);
      close();
      return;
    }
    close();
    navigate(location.pathname);
  };
  return (
    <Dialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      title={t('eleve:actions.addEleve')}
    >
      <AddEleveDialogBody onClose={onClose} />
    </Dialog>
  );
}
